/**
 * CalculationReviewPanel — Phase 07
 *
 * The main review shell that wraps every engine's existing display card
 * and adds approve/dispute/rerun affordances.
 *
 * Per a6-review-panel-primitive.md: one shell, many contents.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';

import ReviewStatusPill from './ReviewStatusPill';
import ReviewActionBar from './ReviewActionBar';
import ApproveModal from './ApproveModal';
import type { ApproveVarianceContext } from './ApproveModal';
import { api } from '../../../utils/api';
import { formatCurrency } from '../../../utils/formatters';
import DisputeModal from './DisputeModal';
import WaiveModal from './WaiveModal';
import ActiveOverridesList from './ActiveOverridesList';
import OverrideFormModal from './OverrideFormModal';
import { useRoleCapabilities } from '../../../hooks/useRoleCapabilities';
import VerifierStatusBanner from './VerifierStatusBanner';
import ConsensusSummary from './ConsensusSummary';
import UpstreamProvenance from './UpstreamProvenance';
import AttestationHistoryPanel from './AttestationHistoryPanel';

import {
  getEngineReview,
  getEngineReviewExternal,
  startReview,
  approveEngine,
  disputeEngine,
  requestRerun,
  waiveEngine,
  startReviewInHouse,
  approveEngineInHouse,
  disputeEngineInHouse,
  waiveEngineInHouse,
  requestRerunInHouse,
  addOverride,
  deactivateOverride,
} from '../../../services/engineReviewApi';

import type {
  EngineId,
  EngineReviewStatus,
  EngineReviewState,
  ReviewPanelMode,
  ApprovePayload,
  DisputePayload,
  RerunPayload,
  WaivePayload,
  EngineOverride,
  OverridePayload,
  OverrideSuggestion,
} from '../../../types/engineReview';
import { ENGINE_NAMES } from '../../../types/engineReview';

// =============================================================================
// LOADING SKELETON
// =============================================================================

function LoadingSkeleton() {
  return (
    <div
      className="rounded-lg border shadow-sm animate-pulse"
      style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
      data-testid="review-panel-loading"
    >
      <div className="p-4 border-b" style={{ borderColor: 'var(--color-border)' }}>
        <div className="flex items-center gap-3">
          <div className="h-6 w-24 rounded-[2px]" style={{ backgroundColor: 'var(--color-bg-elevated)' }} />
          <div className="h-5 w-48 rounded" style={{ backgroundColor: 'var(--color-bg-elevated)' }} />
        </div>
        <div className="mt-2 h-4 w-32 rounded" style={{ backgroundColor: 'var(--color-bg-elevated)' }} />
      </div>
      <div className="p-4 space-y-4">
        <div className="h-16 rounded" style={{ backgroundColor: 'var(--color-bg-hover)' }} />
        <div className="h-24 rounded" style={{ backgroundColor: 'var(--color-bg-hover)' }} />
        <div className="h-32 rounded" style={{ backgroundColor: 'var(--color-bg-hover)' }} />
      </div>
      <div className="p-4 border-t flex gap-3" style={{ borderColor: 'var(--color-border)' }}>
        <div className="h-8 w-20 rounded" style={{ backgroundColor: 'var(--color-bg-elevated)' }} />
        <div className="h-8 w-20 rounded" style={{ backgroundColor: 'var(--color-bg-elevated)' }} />
        <div className="h-8 w-28 rounded" style={{ backgroundColor: 'var(--color-bg-elevated)' }} />
        <div className="h-8 w-16 rounded" style={{ backgroundColor: 'var(--color-bg-elevated)' }} />
      </div>
    </div>
  );
}

// =============================================================================
// ERROR STATE
// =============================================================================

interface ErrorStateProps {
  error: string;
  onRetry: () => void;
}

function ErrorState({ error, onRetry }: ErrorStateProps) {
  return (
    <div
      className="rounded-[2px] border p-6 text-center"
      style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
      data-testid="review-panel-error"
    >
      <svg
        className="w-12 h-12 mx-auto mb-4"
        style={{ color: 'var(--color-blocker)' }}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
        />
      </svg>
      <h3 className="text-lg font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>
        Failed to Load Review Data
      </h3>
      <p className="text-sm mb-4" style={{ color: 'var(--color-text-muted)' }}>{error}</p>
      <button
        onClick={onRetry}
        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg focus:outline-none"
        style={{ color: 'var(--color-on-brand)', backgroundColor: 'var(--color-brand)' }}
        data-testid="review-panel-retry"
      >
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
          />
        </svg>
        Retry
      </button>
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export interface CalculationReviewPanelProps {
  /** Report ID (claim UUID) */
  reportId: string;
  /** Engine ID */
  engineId: EngineId;
  /** Optional initial state for testing/storybook */
  initialReviewState?: EngineReviewStatus;
  /** Callback when state changes */
  onStateChange?: (newState: EngineReviewState) => void;
  /** The existing engine-specific display card goes here */
  children: React.ReactNode;
  /** Default expanded state for collapsible sections */
  defaultExpanded?: boolean;
  /** Whether to show upstream provenance */
  showProvenance?: boolean;
  /** Display mode */
  mode?: ReviewPanelMode;
  /** Forensic Assistant deep-link. When present (and `mode` is
   * `in_house_edit`), the override modal auto-opens pre-filled from the AI
   * proposal and provenance-tagged `from_ai_suggestion`. The human still
   * attests (≥10-char note) before applying — this only seeds the form. */
  overrideSuggestion?: OverrideSuggestion | null;
}

export default function CalculationReviewPanel({
  reportId,
  engineId,
  initialReviewState,
  onStateChange,
  children,
  defaultExpanded = true,
  showProvenance = true,
  mode = 'external',
  overrideSuggestion = null,
}: CalculationReviewPanelProps) {
  // ==========================================================================
  // STATE
  // ==========================================================================

  const [reviewStatus, setReviewStatus] = useState<EngineReviewStatus | null>(
    initialReviewState || null,
  );
  const [loading, setLoading] = useState(!initialReviewState);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Modal states
  const [showApproveModal, setShowApproveModal] = useState(false);
  // dual_method: out-of-tolerance variance context (drives the REQUIRED attribution on
  // approve). Undefined for other engines / in-tolerance / fetch error → modal unchanged.
  const [varianceContext, setVarianceContext] = useState<ApproveVarianceContext | undefined>(undefined);
  const [showDisputeModal, setShowDisputeModal] = useState(false);
  const [showWaiveModal, setShowWaiveModal] = useState(false);
  // Phase 07.6 — override form modal state.
  const [showOverrideModal, setShowOverrideModal] = useState(false);

  const engineName = ENGINE_NAMES[engineId];

  // ==========================================================================
  // GAP-P1-ROLECAP — role-capability gating (additive over the mode prop)
  // ==========================================================================
  // `role === null` means NO auth backend was detectable (no Clerk mount,
  // no attorney/accountant JWT context). The legacy accountant surfaces —
  // and every pre-ROLECAP test — reach this panel exactly that way, already
  // protected by route guards + the `mode` prop, and the backend enforces
  // real authorization on every write. Null therefore preserves the
  // pre-ROLECAP mode-driven behavior byte-for-byte. A DETECTED role applies
  // the capability matrix: accountant → full (identical rendering),
  // attorney/adjuster → dispute-only (approve/rerun/waive/override HIDDEN,
  // not disabled), data specialist / unknown → read-only (no action bar).
  const { role, engineReview, canOverride } = useRoleCapabilities();
  const roleKnown = role !== null;
  const disputeOnlyRole = roleKnown && engineReview === 'read+dispute';
  const readOnlyRole = roleKnown && engineReview === 'read';

  // Forensic Assistant deep-link: auto-open the override modal pre-filled
  // when a suggestion arrives for this engine (in-house edit only — the
  // witness/external path doesn't expose override write capability).
  const isInHouseEditMode = mode === 'in_house_edit';
  // GAP-P1-ROLECAP: overrides additionally require the role capability (or
  // an indeterminate role — legacy fallback, see block comment above).
  const overrideAllowed = isInHouseEditMode && (!roleKnown || canOverride);
  useEffect(() => {
    if (overrideSuggestion && overrideAllowed) {
      setShowOverrideModal(true);
    }
  }, [overrideSuggestion, overrideAllowed]);

  // ==========================================================================
  // DATA FETCHING
  // ==========================================================================

  const fetchReviewStatus = useCallback(async () => {
    if (initialReviewState) {
      // Skip fetch if initial state provided (testing/storybook)
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Mode-aware GET dispatch: external mode hits the witness-JWT-gated
      // endpoint shipped in 07.3n. The in-house namespace 401/500s under
      // a witness JWT, so without this dispatch the witness UI never
      // gets past the loading skeleton.
      const fetchFn = mode === 'external' ? getEngineReviewExternal : getEngineReview;
      const data = await fetchFn(reportId, engineId);
      setReviewStatus(data);
    } catch (err) {
      // API 404 / fetch failure: treat as the legitimate "no review
      // records yet" empty state. A fresh report has no review records
      // until an accountant starts reviewing — render the shell with
      // an unreviewed default so children (placeholder cards, engine
      // displays) still mount. Same pragmatic pattern as the landing
      // page fix in PR 240.
      setReviewStatus({
        report_id: reportId,
        engine_id: engineId,
        engine_name: ENGINE_NAMES[engineId],
        status: 'unreviewed',
        overrides_count: 0,
        attestation_history: [],
      });
    } finally {
      setLoading(false);
    }
  }, [reportId, engineId, mode, initialReviewState]);

  useEffect(() => {
    fetchReviewStatus();
  }, [fetchReviewStatus]);

  // dual_method: load the variance context (out-of-tolerance spread + figures) so the
  // approve flow can REQUIRE a court-facing attribution. Fail-open: any error, other
  // engine, or in-tolerance → undefined → the ApproveModal renders unchanged.
  useEffect(() => {
    if (engineId !== 'dual_method') { setVarianceContext(undefined); return; }
    let ignore = false;
    (async () => {
      try {
        const res = await api.get<{ outputs?: Record<string, unknown> }>(
          `/api/v1/engine-review/${reportId}/dual_method/output`,
        );
        const o = (res?.outputs ?? {}) as Record<string, unknown>;
        if (ignore) return;
        if (o.is_within_tolerance === false) {
          const pct = Number(o.variance_pct);
          setVarianceContext({
            isWithinTolerance: false,
            topDown: o.top_down_loss != null ? formatCurrency(o.top_down_loss as string | number) : undefined,
            bottomUp: o.bottom_up_loss != null ? formatCurrency(o.bottom_up_loss as string | number) : undefined,
            spread: o.variance != null ? formatCurrency(o.variance as string | number) : undefined,
            variancePct: Number.isFinite(pct) ? pct : undefined,
          });
        } else {
          setVarianceContext(undefined);
        }
      } catch {
        if (!ignore) setVarianceContext(undefined);
      }
    })();
    return () => { ignore = true; };
  }, [reportId, engineId, reviewStatus?.status]);

  // ==========================================================================
  // ACTION HANDLERS
  // ==========================================================================
  // Mode-aware dispatch: in_house_edit hits the Clerk + data_specialist
  // routes shipped in PR 272; external hits the witness-CPA JWT routes
  // shipped in 07.3b (`backend/app/api/v1/external_accountant.py`).
  const isInHouseEdit = mode === 'in_house_edit';

  const handleStartReview = async () => {
    const startReviewFn = isInHouseEdit ? startReviewInHouse : startReview;
    setIsSubmitting(true);
    try {
      await startReviewFn(reportId, engineId, {});
      toast.success(`Reviewing ${engineName}`);

      if (reviewStatus) {
        setReviewStatus({ ...reviewStatus, status: 'under_review' });
        onStateChange?.('under_review');
      }

      await fetchReviewStatus();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to start review';
      toast.error('Could not start review', { description: message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleApprove = async (payload: ApprovePayload) => {
    const approveFn = isInHouseEdit ? approveEngineInHouse : approveEngine;
    setIsSubmitting(true);
    try {
      await approveFn(reportId, engineId, payload);
      toast.success(`${engineName} approved successfully`);
      setShowApproveModal(false);

      // Optimistic update
      if (reviewStatus) {
        const newStatus: EngineReviewStatus = {
          ...reviewStatus,
          status: 'approved',
        };
        setReviewStatus(newStatus);
        onStateChange?.('approved');
      }

      // Refetch to get accurate server state
      await fetchReviewStatus();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to approve';
      toast.error('Approval failed', { description: message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDispute = async (payload: DisputePayload) => {
    const disputeFn = isInHouseEdit ? disputeEngineInHouse : disputeEngine;
    setIsSubmitting(true);
    try {
      await disputeFn(reportId, engineId, payload);
      toast.success(`${engineName} disputed — routed to in-house queue`);
      setShowDisputeModal(false);

      // Optimistic update
      if (reviewStatus) {
        const newStatus: EngineReviewStatus = {
          ...reviewStatus,
          status: 'disputed',
        };
        setReviewStatus(newStatus);
        onStateChange?.('disputed');
      }

      // Refetch to get accurate server state
      await fetchReviewStatus();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to submit dispute';
      toast.error('Dispute failed', { description: message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRerun = async () => {
    // For MVP, use a simple confirm dialog instead of modal
    const reason = window.prompt('Reason for rerun request:');
    if (!reason) return;

    const payload: RerunPayload = {
      reason,
      cascade_downstream: true,
    };

    const rerunFn = isInHouseEdit ? requestRerunInHouse : requestRerun;
    setIsSubmitting(true);
    try {
      await rerunFn(reportId, engineId, payload);
      toast.success(`${engineName} rerun requested`);

      // Optimistic update
      if (reviewStatus) {
        const newStatus: EngineReviewStatus = {
          ...reviewStatus,
          status: 'rerun_pending',
        };
        setReviewStatus(newStatus);
        onStateChange?.('rerun_pending');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to request rerun';
      toast.error('Rerun request failed', { description: message });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Phase 10 Task 10.5: opens WaiveModal so the reason textarea is wrapped
  // in the CPA privilege banner. Native window.prompt cannot host a React
  // banner, so the prior MVP shortcut produced a guardrail-free write
  // surface. The modal preserves the 30-char minimum and routes the same
  // payload through to handleWaiveSubmit.
  const handleWaive = () => {
    setShowWaiveModal(true);
  };

  const handleWaiveSubmit = async (payload: WaivePayload) => {
    const waiveFn = isInHouseEdit ? waiveEngineInHouse : waiveEngine;
    setIsSubmitting(true);
    try {
      await waiveFn(reportId, engineId, payload);
      toast.success(`${engineName} waived`);
      setShowWaiveModal(false);

      // Optimistic update
      if (reviewStatus) {
        const newStatus: EngineReviewStatus = {
          ...reviewStatus,
          status: 'waived',
        };
        setReviewStatus(newStatus);
        onStateChange?.('waived');
      }

      // Refetch to get accurate server state
      await fetchReviewStatus();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to waive';
      toast.error('Waive failed', { description: message });
    } finally {
      setIsSubmitting(false);
    }
  };

  // ==========================================================================
  // Phase 07.6 — override action handlers (in-house only)
  // ==========================================================================
  // Both handlers gate on `isInHouseEdit` — the witness path doesn't
  // expose override write capability per the attestation chain-of-custody
  // model (witness CPAs sign off on existing data; in-house staff
  // mutates inputs).

  const handleAddOverride = async (payload: OverridePayload) => {
    if (!overrideAllowed) return;
    setIsSubmitting(true);
    try {
      const result = await addOverride(reportId, engineId, payload);
      toast.success(`Override applied to ${payload.field_id}`, {
        description: result.rerun_triggered
          ? `Cascade triggered — ${result.downstream_engines_invalidated.length} downstream engine(s) invalidated.`
          : 'Override saved without cascade.',
      });
      setShowOverrideModal(false);
      await fetchReviewStatus();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to add override';
      toast.error('Override failed', { description: message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInactivateOverride = async (override: EngineOverride) => {
    if (!overrideAllowed) return;
    // Lightweight prompt for the inactivation reason. The form modal
    // pattern is reserved for the create flow (which has 4 fields);
    // inactivation only needs a single string. Same UX shape as
    // handleRerun / handleWaive above.
    const reason = window.prompt(
      `Reason for inactivating override on "${override.field_id}":`,
    );
    if (!reason || !reason.trim()) return;
    setIsSubmitting(true);
    try {
      const result = await deactivateOverride(
        reportId,
        engineId,
        override.override_id,
        reason.trim(),
      );
      toast.success(`Override on ${override.field_id} inactivated`, {
        description: result.rerun_triggered
          ? 'Cascade rerun queued.'
          : undefined,
      });
      await fetchReviewStatus();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to inactivate override';
      toast.error('Inactivation failed', { description: message });
    } finally {
      setIsSubmitting(false);
    }
  };

  // ==========================================================================
  // RENDER
  // ==========================================================================

  // Loading state
  if (loading) {
    return <LoadingSkeleton />;
  }

  // Error state
  if (error) {
    return <ErrorState error={error} onRetry={fetchReviewStatus} />;
  }

  // No data
  if (!reviewStatus) {
    return (
      <ErrorState
        error="No review data available"
        onRetry={fetchReviewStatus}
      />
    );
  }

  const hasVerifierBlockers = (reviewStatus.verifier_status?.checks_failed ?? []).some(
    (c) => c.severity === 'blocker',
  );

  return (
    <div
      className="rounded-[2px] border"
      style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
      data-testid="review-panel"
    >
      {/* Header */}
      <div className="p-4 border-b" style={{ borderColor: 'var(--color-border)' }}>
        <div className="flex items-center gap-3 flex-wrap">
          <ReviewStatusPill
            status={reviewStatus.status}
            reviewerName={reviewStatus.reviewed_by_name}
            testId="review-panel-status"
          />
          {reviewStatus.status === 'unreviewed' && (
            <span className="text-xs px-2 py-0.5 rounded uppercase tracking-wide" style={{ backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}>
              Draft
            </span>
          )}
        </div>
        {reviewStatus.last_run_at && (
          <p className="mt-1 text-sm" style={{ color: 'var(--color-text-muted)' }}>
            Last run: {new Date(reviewStatus.last_run_at).toLocaleString()}
          </p>
        )}
      </div>

      {/* Verifier Banner */}
      {reviewStatus.verifier_status && (
        <div className="px-4 pt-4">
          <VerifierStatusBanner verifier={reviewStatus.verifier_status} />
        </div>
      )}

      {/* Consensus Summary */}
      {reviewStatus.consensus_entries && reviewStatus.consensus_entries.length > 0 && (
        <div className="px-4 pt-4">
          <ConsensusSummary entries={reviewStatus.consensus_entries} />
        </div>
      )}

      {/* Active Overrides — Phase 07.6 surfaces this as a real component
          with edit affordances when mode === 'in_house_edit'. Read-only
          modes still see the chip list, just without the inactivate /
          add buttons. */}
      <ActiveOverridesList
        overrides={reviewStatus.active_overrides ?? []}
        /* GAP-P1-ROLECAP: roles without override capability see the
           read-only chip list even on the in_house_edit surface — the
           add/inactivate affordances are hidden, not disabled. */
        mode={overrideAllowed || !isInHouseEdit ? mode : 'in_house_readonly'}
        disabled={isSubmitting}
        onAddClick={() => setShowOverrideModal(true)}
        onInactivate={handleInactivateOverride}
      />

      {/* Upstream Provenance — Phase 07.3-polish lifted into UpstreamProvenance.tsx */}
      {showProvenance && (
        <div className="px-4 pt-4">
          <UpstreamProvenance upstream={reviewStatus.upstream_engines} />
        </div>
      )}

      {/* Attestation history — Phase 07.3-polish (NEW). Surfaces the
          per-engine action trail that backend already returns in
          `attestation_history` but was previously not rendered anywhere. */}
      {reviewStatus.attestation_history && reviewStatus.attestation_history.length > 0 && (
        <div className="px-4 pt-4">
          <AttestationHistoryPanel history={reviewStatus.attestation_history} />
        </div>
      )}

      {/* Drill-down content (children) */}
      <div className="p-4" data-testid="review-panel-content">
        {children}
      </div>

      {/* Action Bar — mounted in both in-house edit AND witness-external
          modes. Mode-aware dispatch in the handlers above routes each
          action to the right backend (`/engine-review/...` for in-house,
          `/external-accountant/review/...` for witness). Read-only in-house
          mode (`in_house_readonly`) intentionally hides the bar.
          GAP-P1-ROLECAP: read-only ROLES (data specialist / unknown) never
          mount the bar; dispute-only roles (attorney / adjuster) mount it
          with capability="read+dispute" so only the Dispute button renders. */}
      {(mode === 'in_house_edit' || mode === 'external') && !readOnlyRole && (
        <ReviewActionBar
          mode={mode}
          capability={disputeOnlyRole ? 'read+dispute' : 'full'}
          status={reviewStatus.status}
          engineId={engineId}
          verifierReport={reviewStatus.verifier_status}
          isSubmitting={isSubmitting}
          onStartReview={handleStartReview}
          onApprove={() => {
            const hasWarnings = (reviewStatus.verifier_status?.checks_failed ?? []).some(
              (c) => c.severity === 'warning',
            );
            // Force the modal open for an out-of-tolerance dual_method (a required court
            // attribution must be captured) even with no warnings — otherwise the
            // no-warning path would one-click approve and skip the attestation.
            if (hasWarnings || varianceContext) {
              setShowApproveModal(true);
            } else {
              handleApprove({});
            }
          }}
          onDispute={() => setShowDisputeModal(true)}
          onRerun={handleRerun}
          onWaive={handleWaive}
          testIdPrefix="review-action"
        />
      )}

      {/* Modals */}
      <ApproveModal
        isOpen={showApproveModal}
        onClose={() => setShowApproveModal(false)}
        onSubmit={handleApprove}
        engineId={engineId}
        verifierReport={reviewStatus.verifier_status}
        isSubmitting={isSubmitting}
        varianceContext={varianceContext}
        reportId={reportId}
      />

      <DisputeModal
        isOpen={showDisputeModal}
        onClose={() => setShowDisputeModal(false)}
        onSubmit={handleDispute}
        engineId={engineId}
        isSubmitting={isSubmitting}
      />

      <WaiveModal
        isOpen={showWaiveModal}
        onClose={() => setShowWaiveModal(false)}
        onSubmit={handleWaiveSubmit}
        engineId={engineId}
        isSubmitting={isSubmitting}
      />

      {/* Phase 07.6 — override form modal (in-house edit only; GAP-P1-ROLECAP
          additionally requires override capability). The ActiveOverridesList
          "Add override" button toggles this. */}
      {overrideAllowed && (
        <OverrideFormModal
          isOpen={showOverrideModal}
          onClose={() => setShowOverrideModal(false)}
          onSubmit={handleAddOverride}
          engineId={engineId}
          isSubmitting={isSubmitting}
          initialFieldId={overrideSuggestion?.fieldId}
          initialOriginalValue={overrideSuggestion?.originalValue ?? null}
          initialOverrideValue={overrideSuggestion?.suggestedValue ?? null}
          initialOrigin={overrideSuggestion?.origin ?? 'manual'}
          initialPromptedByAiAuditId={overrideSuggestion?.promptedByAiAuditId}
          aiRationale={overrideSuggestion?.rationale}
        />
      )}
    </div>
  );
}

// =============================================================================
// EXPORTS
// =============================================================================

export { CalculationReviewPanel };
